import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSync } from "@/context/SyncContext";
import { SyncBadge } from "@/components/SyncBadge";
import { PhoneInput } from "@/components/PhoneInput";
import { fetchClients, createClient } from "@/lib/data";
import { api } from "@/lib/api";
import { InstallBanner } from "@/components/InstallBanner";
import { shareAppOnWhatsApp } from "@/lib/shareApp";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Search, Plus, User, Users, ClipboardList, Settings, Crown,
  ArrowUpDown, ChevronRight, Loader2, Store, Gift, Share2,
} from "lucide-react";

export default function Dashboard() {
  const { tailleur } = useAuth();
  const { online } = useSync();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ clients: 0, commandes_en_cours: 0, photos: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent"); // recent | alpha
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      const { clients } = await fetchClients();
      setClients(clients);
    } catch (e) {
      toast.error("Impossible de charger les clients");
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await api.get("/dashboard");
      setStats(data);
    } catch {
      /* offline: keep local count */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, online]);

  const filtered = useMemo(() => {
    let list = clients;
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((c) => c.nom.toLowerCase().includes(s) || (c.telephone || "").includes(s));
    }
    list = [...list].sort((a, b) =>
      sort === "alpha" ? a.nom.localeCompare(b.nom) : (b.updatedAt || "").localeCompare(a.updatedAt || "")
    );
    return list;
  }, [clients, q, sort]);

  const clientCount = clients.length || stats.clients;

  return (
    <div className="min-h-screen bg-tp-bg pb-28">
      {/* Header */}
      <div className="bg-tp-green text-white px-5 pt-6 pb-16 rounded-b-3xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-tp-gold flex items-center justify-center shrink-0">
              <Store size={20} className="text-tp-ink" />
            </div>
            <div className="min-w-0">
              <p className="text-white/70 text-xs">Bonjour,</p>
              <p className="font-head font-bold text-lg truncate">{tailleur?.atelier || tailleur?.nom || "Mon atelier"}</p>
            </div>
          </div>
          <button
            data-testid="account-btn"
            onClick={() => navigate("/compte")}
            className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"
          >
            <Settings size={20} />
          </button>
        </div>
        <div className="mt-3">
          <SyncBadge />
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 -mt-10">
        <div className="grid grid-cols-2 gap-3">
          <StatCard testId="stat-clients" icon={Users} label="Clients" value={clientCount} />
          <StatCard testId="stat-commandes" icon={ClipboardList} label="Commandes en cours" value={stats.commandes_en_cours} />
        </div>
        {tailleur?.plan === "gratuit" && (
          <button
            data-testid="pro-banner"
            onClick={() => navigate("/pro")}
            className="mt-3 w-full flex items-center justify-between bg-tp-gold/15 border border-tp-gold/40 rounded-xl px-4 py-3"
          >
            <span className="flex items-center gap-2 text-tp-ink font-semibold text-sm">
              <Crown size={18} className="text-tp-gold" /> Plan Gratuit · {clientCount}/{stats?.limites?.clients || 30} clients
            </span>
            <span className="text-tp-green font-bold text-sm">Passer Pro</span>
          </button>
        )}
        <button
          data-testid="referral-banner"
          onClick={() => navigate("/parrainage")}
          className="mt-3 w-full flex items-center justify-between bg-tp-green/10 border border-tp-green/30 rounded-xl px-4 py-3"
        >
          <span className="flex items-center gap-2 text-tp-ink font-semibold text-sm">
            <Gift size={18} className="text-tp-green" /> Parrainez & gagnez des clients gratuits
          </span>
          <ChevronRight size={18} className="text-tp-green" />
        </button>
        <InstallBanner />
        <button
          data-testid="share-app-banner"
          onClick={() => shareAppOnWhatsApp(tailleur)}
          className="mt-3 w-full flex items-center justify-between bg-tp-whatsapp rounded-xl px-4 py-3 text-white active:scale-[0.99] transition-transform"
        >
          <span className="flex items-center gap-2 font-semibold text-sm">
            <Share2 size={18} /> Partager Tailleur Pro sur WhatsApp
          </span>
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Search + sort */}
      <div className="px-5 mt-5 flex gap-2">
        <div className="flex-1 relative">
          <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-tp-muted" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un client…"
            className="w-full h-[52px] rounded-xl bg-white border border-gray-300 pl-11 pr-4 text-base outline-none focus:border-tp-green"
          />
        </div>
        <button
          data-testid="sort-btn"
          onClick={() => setSort(sort === "recent" ? "alpha" : "recent")}
          className="w-[52px] h-[52px] rounded-xl bg-white border border-gray-300 flex items-center justify-center"
          title={sort === "recent" ? "Trier A-Z" : "Trier par récent"}
        >
          <ArrowUpDown size={20} className="text-tp-green" />
        </button>
      </div>
      <p className="px-5 mt-3 text-xs text-tp-muted uppercase tracking-wider font-semibold">
        {sort === "alpha" ? "Ordre alphabétique" : "Clients récents"} · {filtered.length}
      </p>

      {/* Client list */}
      <div className="px-5 mt-2 space-y-2.5">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-tp-green" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState hasClients={clients.length > 0} onAdd={() => setShowAdd(true)} />
        ) : (
          filtered.map((c) => <ClientRow key={c.id} client={c} onClick={() => navigate(`/client/${c.id}`)} />)
        )}
      </div>

      {/* FAB */}
      <button
        data-testid="fab-new-client"
        onClick={() => setShowAdd(true)}
        className="fixed bottom-6 left-1/2 -translate-x-1/2 max-w-md w-[calc(100%-2.5rem)] mx-auto h-14 rounded-full bg-tp-green hover:bg-tp-greenDark text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-tp-green/30 active:scale-[0.98] transition-transform"
      >
        <Plus size={22} /> Nouveau client
      </button>

      <AddClientDialog open={showAdd} onOpenChange={setShowAdd} onCreated={(c) => navigate(`/client/${c.id}`)} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
      <Icon size={22} className="text-tp-green mb-2" />
      <p className="font-head text-3xl font-extrabold text-tp-ink">{value}</p>
      <p className="text-sm text-tp-muted">{label}</p>
    </div>
  );
}

function ClientRow({ client, onClick }) {
  return (
    <button
      data-testid={`client-row-${client.id}`}
      onClick={onClick}
      className="w-full bg-white rounded-xl p-3.5 border border-gray-100 flex items-center gap-3 text-left active:scale-[0.99] transition-transform"
    >
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${client.sexe === "F" ? "bg-pink-100" : "bg-tp-green/10"}`}>
        <User size={20} className={client.sexe === "F" ? "text-pink-500" : "text-tp-green"} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-tp-ink truncate">{client.nom}</p>
        <p className="text-sm text-tp-muted truncate">{client.telephone || "Pas de numéro"}</p>
      </div>
      <ChevronRight size={20} className="text-gray-300" />
    </button>
  );
}

function EmptyState({ hasClients, onAdd }) {
  return (
    <div className="text-center py-14">
      <div className="w-16 h-16 rounded-full bg-tp-green/10 flex items-center justify-center mx-auto mb-4">
        <Users size={28} className="text-tp-green" />
      </div>
      <p className="font-head font-bold text-tp-ink text-lg">{hasClients ? "Aucun résultat" : "Aucun client"}</p>
      <p className="text-tp-muted mt-1 text-base">
        {hasClients ? "Essayez un autre nom." : "Ajoutez votre premier client pour commencer."}
      </p>
      {!hasClients && (
        <Button data-testid="empty-add-btn" onClick={onAdd} className="mt-5 h-12 rounded-full bg-tp-green px-6 font-bold">
          <Plus size={20} /> Ajouter un client
        </Button>
      )}
    </div>
  );
}

export function AddClientDialog({ open, onOpenChange, onCreated }) {
  const [nom, setNom] = useState("");
  const [telephone, setTelephone] = useState("+221");
  const [sexe, setSexe] = useState("H");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!nom.trim()) return toast.error("Le nom est obligatoire");
    setSaving(true);
    try {
      const c = await createClient({ nom, telephone: telephone === "+221" ? "" : telephone, sexe, note });
      toast.success("Client ajouté");
      onOpenChange(false);
      setNom(""); setTelephone("+221"); setSexe("H"); setNote("");
      onCreated?.(c);
    } catch (e) {
      const msg = e?.response?.status === 402 ? "Limite gratuite atteinte (30 clients)." : "Erreur lors de l'ajout";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Nouveau client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <label className="font-semibold text-tp-ink block mb-1.5 text-sm">Nom complet *</label>
            <input
              data-testid="client-nom-input"
              value={nom}
              autoFocus
              onChange={(e) => setNom(e.target.value)}
              placeholder="Ex: Awa Ndiaye"
              className="w-full h-12 rounded-xl bg-white border border-gray-300 px-4 text-base outline-none focus:border-tp-green"
            />
          </div>
          <div>
            <label className="font-semibold text-tp-ink block mb-1.5 text-sm">Téléphone</label>
            <PhoneInput value={telephone} onChange={setTelephone} testId="client-phone" />
          </div>
          <div>
            <label className="font-semibold text-tp-ink block mb-1.5 text-sm">Sexe</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: "H", l: "Homme" }, { v: "F", l: "Femme" }].map((o) => (
                <button
                  key={o.v}
                  data-testid={`sexe-${o.v}`}
                  onClick={() => setSexe(o.v)}
                  className={`h-12 rounded-xl border font-semibold text-base transition-colors ${
                    sexe === o.v ? "bg-tp-green text-white border-tp-green" : "bg-white text-tp-ink border-gray-300"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="font-semibold text-tp-ink block mb-1.5 text-sm">Note</label>
            <textarea
              data-testid="client-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: préfère les cols mao, quartier Médina"
              rows={2}
              className="w-full rounded-xl bg-white border border-gray-300 px-4 py-3 text-base outline-none focus:border-tp-green resize-none"
            />
          </div>
          <Button
            data-testid="save-client-btn"
            onClick={submit}
            disabled={saving}
            className="w-full h-[52px] rounded-full bg-tp-green hover:bg-tp-greenDark font-bold text-base"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : "Enregistrer le client"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
